import PropTypes from 'prop-types';

import Textarea from './Textarea';

/**
 * Textarea with a live character counter.
 *
 * Neither `Textarea` here nor the one in `packages/ui` has a counter, and a
 * length-capped required field needs one — a user who only discovers the cap when
 * the server rejects their text has already lost the work. Kept in `apps/app`
 * rather than promoted to `packages/ui` until a second consumer exists.
 *
 * `helperText` renders on the left of the counter row rather than through
 * `Textarea`'s own helper slot, so the two share a line instead of stacking.
 */
const TextareaWithCounter = ({
  id,
  label,
  value,
  onChange,
  maxLength,
  helperText,
  helperClassName,
  error,
  required,
  rows,
  containerClassName,
  ...rest
}) => {
  const length = value?.length ?? 0;
  const overLimit = length > maxLength;

  return (
    <div className={containerClassName}>
      <Textarea
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        required={required}
        rows={rows}
        error={error || overLimit}
        maxLength={maxLength}
        {...rest}
      />

      <div className="mt-1.5 flex items-start justify-between gap-3">
        <p className={`text-xs ${helperClassName || 'text-gray-600'}`}>{helperText}</p>

        {/* aria-live so a screen reader hears the count approach the cap rather
            than only discovering it on rejection. */}
        <p
          aria-live="polite"
          className={`shrink-0 text-xs ${overLimit ? 'font-semibold text-red-500' : 'text-gray-500'}`}
        >
          {length} / {maxLength}
        </p>
      </div>
    </div>
  );
};

TextareaWithCounter.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number.isRequired,
  helperText: PropTypes.node,
  helperClassName: PropTypes.string,
  error: PropTypes.bool,
  required: PropTypes.bool,
  rows: PropTypes.number,
  containerClassName: PropTypes.string,
};

TextareaWithCounter.defaultProps = {
  value: '',
  helperText: '',
  helperClassName: '',
  error: false,
  required: false,
  rows: 4,
  containerClassName: '',
};

export default TextareaWithCounter;
